import React from 'react';
import { useNavigate } from 'react-router-dom';

import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * 生成AI考察記事
 * UI仕様: 「新書を一冊読む体験」をWebで再現する。装飾を削ぎ落とし、テキストそのものに集中させる。
 */
const ThoughtsAi: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Stack spacing={0} sx={{ pb: 6 }}>
      {/* パンくず */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 16 }} />}>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            color="text.secondary"
            onClick={() => navigate('/devdocs')}
            sx={{ cursor: 'pointer' }}
          >
            Dev Insights
          </Link>
          <Typography variant="body2" color="text.primary">
            生成AIは道具か、それとも自分自身か
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* 記事本文コンテナ */}
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          px: 2,
          pt: 10,
          pb: 15,
          fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
          color: '#1A1A1A',
          bgcolor: '#FAFAF7',
          borderRadius: 2,
          boxShadow: '0 0 40px rgba(0,0,0,0.04)',
        }}
      >
        {/* タイトル */}
        <Typography
          component="h1"
          sx={{
            fontSize: 24,
            fontWeight: 700,
            fontFamily: 'inherit',
            textAlign: 'center',
            lineHeight: 1.6,
            mb: 2,
          }}
        >
          生成AIは道具か、それとも自分自身か
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: 'inherit',
            textAlign: 'center',
            color: '#555',
            mb: 4,
          }}
        >
          認知機能の外部化についての考察
        </Typography>
        <Box sx={{ borderBottom: '1px solid #D0CCC5', mb: 7.5 }} />

        {/* はじめに */}
        <ChapterHeading number="はじめに" />
        <Paragraph>
          生成AIは我々の仕事を奪うのか、それとも人間を拡張するのか。そういう問いの立て方をよく見かける。しかし筆者にはそのどちらもしっくりこない。
        </Paragraph>
        <Paragraph>
          世間では「AIを使えば誰でも生産性が上がる」「経験がなくても同じように開発できる」と言われている。本当だろうか。電卓が登場したとき、計算ができない人が突然数学者になっただろうか。インターネットが普及したとき、Googleを使えるだけで専門家になれただろうか。
        </Paragraph>
        <Paragraph>
          生成AIとは結局のところ何なのか。どう付き合えばいいのか。万人に効く「正しい使い方」は存在するのか。本稿ではこれらの問いを、文字の発明から一九七〇年代の電卓論争まで、歴史的な前例と照らし合わせながら考えていく。
        </Paragraph>

        {/* 第一章 */}
        <ChapterHeading number="第一章" title="文字、計算機、そして生成AI" />
        <Paragraph>
          人類が最初に認知を外部へ預けたのは、文字の発明においてだった。紀元前三二〇〇年頃、メソポタミアのシュメール人は穀物や家畜の数量を記録するために楔形文字を生み出した。目的は会計帳簿だった。しかしこの実務的な道具は、やがて本来の用途を超えて学術や文学に転用され、人間の記憶力では到底保持できない規模の知識を蓄積することを可能にした。
        </Paragraph>
        <Paragraph>
          次に外部化されたのは計算だ。パスカルやライプニッツの機械式計算機から、現代の電子計算機に至るまで、人間は「頭の中で計算する」という認知作業を機械に委ねてきた。
        </Paragraph>
        <Paragraph>
          そしてインターネット。膨大な情報の海から目的のものを探し出すという認知作業——かつては図書館で何時間もかけていた作業——が、検索エンジンによって瞬時に行えるようになった。
        </Paragraph>
        <Paragraph>
          これらに共通するのは、いずれも人間の認知機能の一部を外側に預けたということだ。肉体労働を代替する道具や家電とは本質的に異なる。洗濯機は「洗う」という労働を代替したが、「何を洗うべきか判断する」認知は代替していない。
        </Paragraph>
        <Paragraph>
          生成AIもこの系譜の延長線上にある。ただし決定的に違うのは、今まで各技術が外部化した後にも「人間に残っていた部分」——つまり「どういう情報を入れればよいか」「情報をどう扱うか」「どういう形でアウトプットするか」というメタ認知的な部分——までも外部化している点にある。
        </Paragraph>
        <ArticleTable
          headers={['技術', '外部化した認知機能', '人間に残っていた部分']}
          rows={[
            ['文字', '記憶', '記録を読み解く、活用する'],
            ['計算機', '計算', '何を計算するか決める、結果を解釈する'],
            ['インターネット', '情報探索', '何を探すか決める、取捨選択する'],
            ['生成AI', '上記全て + 「人間に残っていた部分」そのもの', '？'],
          ]}
        />
        <Paragraph>
          実際に成果物ができてしまうから労働力の外部化と勘違いされるが、そこは本質ではない。生成AIは「労働力の外部化」ではなく「認知機能の外部化」である。この区別が、以降の議論の出発点になる。
        </Paragraph>

        {/* 第二章 */}
        <ChapterHeading number="第二章" title="メガネと生成AIの決定的な違い" />
        <Paragraph>
          第一章で述べたように、認知機能の外部化は文字の時代から行われてきた。しかしこれまでの外部化には、ある共通点があった。自分がやった部分と道具がやった部分の境界が明確だったということだ。
        </Paragraph>
        <Paragraph>
          計算機であれば「入力→処理→出力」が明確に分離されていた。式を入れたのは自分、計算したのは計算機、結果を解釈するのは自分。この三つの領域は混ざりようがない。インターネットでも同様で、検索キーワードを入れたのは自分、情報を表示したのはGoogle、どの情報を採用するか決めたのは自分だ。
        </Paragraph>
        <Paragraph>
          しかし生成AIでは、思考プロセスそのものが外部化されている。「こういう設計書を書いて」と指示して成果物が出てきたとき、「どこまでが自分の認知で、どこからがAIの認知か」を分離できない。設計の方向性を決めたのは自分だが、具体化したのはAIで、でもその具体化の仕方は自分の指示に基づいていて——境界がグラデーションになる。
        </Paragraph>
        <Paragraph>
          メガネの比喩を持ち出してみよう。メガネをかけて自分の視力が良くなったと思う人はいない。外せばぼやけるという即座のフィードバックがあるからだ。「あ、今自分はメガネに補助されている」と常に自覚できる。
        </Paragraph>
        <Paragraph>
          しかし生成AIには「外した状態」が見えにくい。一時間考えて作った文章も、AIに作らせた文章も、成果物としては同じに見える。完成した設計書を前にしたとき、それを自力で作った場合と何が違うのかを感覚的に区別する手がかりがない。だからAIの能力を自分の能力だと錯覚しやすい。
        </Paragraph>
        <Paragraph>
          これは<Bold>自己認知のキャリブレーション（較正）が壊れる</Bold>
          問題と言える。自分は今どのくらいの能力を持っていて、どのくらいを道具に頼っているのか。その測定器が狂う。
        </Paragraph>

        {/* 第三章 */}
        <ChapterHeading number="第三章" title="二四〇〇年前のソクラテスと同じ問い" />
        <Paragraph>
          プラトンの対話篇『パイドロス』に、文字の発明にまつわる寓話が登場する。
        </Paragraph>
        <Paragraph>
          エジプトの古い神テウトは、算術・幾何学・天文学、そして文字を発明した。テウトはエジプト全土に君臨する王の神タモスのもとを訪れ、自らの発明を披露する。文字について、テウトはこう語った。
        </Paragraph>
        <Blockquote>
          「王様、この文字というものを学べば、エジプト人たちの知恵は高まり、もの覚えはよくなるでしょう。私の発見したのは、記憶と知恵の秘訣なのですから」
        </Blockquote>
        <Paragraph>しかし王タモスはこれを退けた。</Paragraph>
        <Blockquote>
          「たぐいなき技術の主テウトよ、技術上の事柄を生み出す力をもった人と、生み出された技術がそれを使う人々にどのような害をあたえ、どのような益をもたらすかを判別する力をもった人とは、別の者なのだ」
        </Blockquote>
        <Paragraph>
          タモスの批判の核心はこうだ。文字を学んだ者は記憶力の訓練をおろそかにし、自分の内から思い出すのではなく外部に刻まれた記号に頼って思い出すようになる。文字の発明は「記憶の秘訣」ではなく「想起の秘訣」——つまり記憶そのものではなく、記憶を呼び起こすための補助に過ぎない。そして文字を学んでも見かけだけ博識になるだけで、うぬぼれだけが肥大するだろう、と。
        </Paragraph>
        <Paragraph>
          この寓話を対話の中で語ったのはソクラテスであり、彼自身も文字（書き言葉）に対して否定的な立場をとっていた。ソクラテスは「ものを書くというのは絵画の場合とよく似ている」と述べ、書かれた言葉は話しかけても返事が返ってこない片方向のコミュニケーションでしかなく、本当の知は対話（ディアレクティケー）を通じてのみ生まれると主張した。
        </Paragraph>
        <Paragraph>
          そして皮肉なことに、文字を批判したソクラテスのこの主張は、弟子プラトンが「文字で書き残した」おかげで二四〇〇年後の今も読むことができる。ソクラテス自身は一切の著作を残さなかった。文字による記録がなければ、彼の思想は口伝の途絶とともに消えていたはずだ。文字の限界を説いた言葉そのものが、文字の力によって不滅になっている。ソクラテスが今生きていたら、この記事がAIとの対話で生成されていることにも怒るだろうか。
        </Paragraph>
        <Paragraph>この構造は生成AIにそのまま当てはまる。</Paragraph>
        <ArticleTable
          headers={['', '文字', '生成AI']}
          rows={[
            ['楽観派（テウト）', '記憶力が拡張される', '認知能力が拡張される'],
            ['懐疑派（タモス）', '記憶力が退化する', '思考力が退化する'],
            ['歴史的結果', '記憶力は退化したが、文明は発展した', '？'],
          ]}
        />
        <Paragraph>
          文字によって個人の記憶力は確かに退化した。しかし知識の世代間継承が可能になり、人類全体としては飛躍的に発展した。生成AIでも同じことが起きるかもしれないし、起きないかもしれない。良い悪いの二項対立では捉えられない。
        </Paragraph>

        {/* 第四章 */}
        <ChapterHeading number="第四章" title="品質のために使う——「楽」のためではなく" />
        <Paragraph>
          ここまで、生成AIの本質と危険性について論じてきた。では生成AIは使うべきではないのか。そんなことはない。問題は「使うか使わないか」ではなく「何のために使うか」だ。
        </Paragraph>
        <Paragraph>
          システム開発の現場を想像してほしい。品質・生産性・コストのトレードオフがある。理想を言えば、要件定義書は完璧に書きたいし、設計書も全て揃えたいし、テストも網羅的にやりたい。しかし現実には期限がある。予算がある。人が足りない。
        </Paragraph>
        <Paragraph>
          だから本来作るべき設計書を省略する。やるべきテストを除外する。バグが見つかってすぐに修正して環境にあげなければならないとき、要件定義書から直している余裕はない。そうやって徐々に、仕様書と設計書と実装が乖離していく。みんな「本当はやった方がいい」と分かっていながら、できない。よくある話だ。
        </Paragraph>
        <Paragraph>
          生成AIは、このジレンマに対する一つの解を提供する。
          <Bold>今まで「コスト的に諦めていた部分」を諦めなくてよくなる</Bold>のだ。
        </Paragraph>
        <BulletList
          items={[
            'AIが担う：生産（設計書を書く、コードを書く、テストを書く等の作業）',
            '人間が担う：品質管理（AIの成果物が正しいか、設計として筋が通っているか、仕様との整合性があるか）',
          ]}
        />
        <Paragraph>
          生成AIは生産性を上げるための道具だが、その目的は「楽をすること」ではない。「今まで妥協していた品質水準を妥協しなくて済むようにすること」だ。AIによって浮いた時間とコストを、品質の担保に回す。これが筆者の考える活用基準である。
        </Paragraph>

        {/* 第五章 */}
        <ChapterHeading number="第五章" title="経験者ほど恩恵を受けるという逆説" />
        <Paragraph>
          第四章で述べた構造——AIが生産を担い、人間が品質管理を担う——を受け入れるとき、必然的に一つの帰結が見えてくる。人間に求められる能力は「作れる力」から「見抜く力」にシフトする。
        </Paragraph>
        <Paragraph>
          コードを書ける力より、AIが書いたコードのバグや設計上の問題を見抜ける力のほうが重要になる。設計書を書ける力より、AIが書いた設計書に潜む論理的な矛盾や仕様との乖離を発見できる力のほうが重要になる。
        </Paragraph>
        <Paragraph>
          ここで「それなら経験がなくても、AIが書いてくれるから同じように開発できるのでは？」という問いが出てくる。世の中でよく言われている方向性だ。しかし筆者はこれを正しくないと考える。
        </Paragraph>
        <Paragraph>
          なぜか。「見抜く力」は経験なしには持ち得ないからだ。設計のアンチパターンを見抜けるのは、過去に自分がそのパターンで痛い目に遭ったことがある人だ。テストケースの漏れに気づけるのは、過去にテスト漏れで障害を出した経験がある人だ。経験者にしか品質は担保できない。
        </Paragraph>
        <Paragraph>
          むしろ逆説的に、経験がある人ほどAIの恩恵が大きくなる。見抜く力がある人がAIに量産させれば品質と生産性を両立できるが、見抜く力がない人がAIに量産させると一見動くけど品質の低いものが大量に生まれる（動くけど誰もメンテできないコードが生まれる現象は、AI以前から存在していたが）。
        </Paragraph>
        <Paragraph>
          したがって「生成AIを使えば経験がある人でもない人でも同じように開発できる」という方向性は、むしろ危険ですらある。
        </Paragraph>

        {/* 第六章 */}
        <ChapterHeading number="第六章" title="一九七〇年代の電卓論争が教えてくれること" />
        <Paragraph>
          「見抜く力」は自分で苦労して作った経験がないと身につかないのか。この問いに対する明確な答えはまだない。しかし歴史的な参照点はある。
        </Paragraph>

        <SectionHeading title="電卓が教室に入った日" />
        <Paragraph>
          一九七〇年代に電卓が教育現場に入った際、大きな論争が起きた。Mathematics
          Teacher誌が一九七〇年代半ばに行った調査では、回答者（主に教師と数学者）の七二%が七年生（中一相当）に電卓を持たせることに反対した。「基礎的な計算力が育たなくなる」「計算の手順を理解しないまま答えだけ得ても意味がない」という懸念だった。
        </Paragraph>
        <Paragraph>実際にその後何が起きたか。</Paragraph>
        <BulletList
          items={[
            '計算ドリルの量は確かに減った',
            'しかしその分、概念理解や証明の論理に時間を割けるようになった',
            'コンピュータで数値実験をして直感を得てから、手で証明するというハイブリッドなアプローチが生まれた',
            '計算機登場後に生まれた数学者も、数学の知識を立派に身につけている。数学のレベルが下がったわけではない',
          ]}
        />
        <Paragraph>
          つまり「手計算で大量に失敗する」という学習経験は減ったが、「コンピュータの結果を疑って検証する」「なぜそうなるか自分で証明する」という別の形の訓練に置き換わった。学習のルートが変わっただけで、到達点は維持された。
        </Paragraph>

        <SectionHeading title="五十年後の反復" />
        <Paragraph>
          この一九七〇年代の電卓論争は、現在の生成AI導入を巡る論争と構造が酷似している。
        </Paragraph>
        <ArticleTable
          headers={['', '一九七〇年代の電卓論争', '現在の生成AI論争']}
          rows={[
            ['懸念', '基礎計算力が育たなくなる', '基礎的な設計力・コーディング力が育たなくなる'],
            [
              '批判',
              '手順を理解せず答えだけ得ても意味がない',
              'コードの意味を理解せず動くものが出ても意味がない',
            ],
            ['キーワード', '計算力の退化', '技術的負債、認知的負債、品質低下'],
            ['反対派', '電卓は禁止すべき/制限すべき', 'AIは禁止すべき/制限すべき'],
            [
              '推進派',
              '計算から解放されれば高次の思考に集中できる',
              '実装から解放されれば高次の設計・判断に集中できる',
            ],
          ]}
        />
        <Paragraph>
          電卓の場合、最終的には「全面禁止も全面許可もせず、使い方のリテラシーを教える」方向に落ち着いた。生成AIもある程度は同じ帰結を辿るだろう。しかし最終的な着地点は電卓とは異なるものになる。
        </Paragraph>

        {/* 第七章 */}
        <ChapterHeading number="第七章" title="人間の脳が持つ構造的な限界" />
        <Paragraph>
          電卓と生成AIで決定的に異なるのは、<Bold>外部化の境界の明確さ</Bold>だ。
        </Paragraph>
        <Paragraph>
          電卓は「3+5を入力して8が出る」——操作が単純で入出力の境界が明確だから、「ここは電卓に任せる、ここは自分で考える」と線引きができた。電卓の結果を見て「自分の計算力が上がった」と錯覚する人はいない。
        </Paragraph>
        <Paragraph>
          しかし生成AIは「複雑な指示を出して複雑な成果物が返ってくる」。ここに人間の認知バイアスが介入する。そして重要なのは、これは個人の怠慢や未熟さの問題ではなく、
          <Bold>人間の脳の構造的な限界</Bold>
          だということだ。人間の認知は元来、自分と道具の境界を正確に識別するようにはできていない。生成AIという道具の性質が、この認知の限界と噛み合ってしまう。
        </Paragraph>
        <BulletList
          items={[
            '自己奉仕バイアス — 良い結果は自分の手柄にする傾向。AIで良い成果物が出ると「自分の指示が良かったから」と思い込む',
            'ダニング＝クルーガー効果 — 能力が低い人ほど自分の能力を過大評価する。AIが補完してくれている部分に気づかない',
            'IKEA効果 — 少しでも自分が関与したものに対して価値を過大評価する。プロンプトを書いただけで「自分が作った」感覚を持つ（IKEAの棚を組み立てただけで大工になった気分になるのと同じだ）',
            '確証バイアス — AIの出力が自分の期待と一致していれば疑わない',
          ]}
        />
        <Paragraph>
          これらのバイアスは人間の脳の構造的な性質であり、排除することはできない。だから「使い方のリテラシー」を教えるだけでは不十分で、
          <Bold>「この錯覚に自覚的であること」が生成AIと付き合う上での前提条件</Bold>
          になる。
        </Paragraph>
        <Paragraph>
          自覚がある人は疑う姿勢を持てる。疑う姿勢があれば見抜く力が育つ。自覚がない人はバイアスに流されてAIの出力を無批判に受け入れ、見抜く力は育たない。
        </Paragraph>

        {/* 第八章 */}
        <ChapterHeading number="第八章" title="言語化と抽象化——「見抜く力」の正体" />

        <SectionHeading title="二つの構成要素" />
        <Paragraph>「見抜く力」を分解すると、二つの能力から構成される。</Paragraph>
        <Paragraph>
          一つは<Bold>言語化力</Bold>
          。「なぜそうなっているか」「なぜそうすべきか」を言葉にできる力だ。
        </Paragraph>
        <Paragraph>
          もう一つは<Bold>抽象化・再文脈化力</Bold>
          。具体的な事象から本質（抽象概念）を抽出し、それを自分の目的に合った新しい文脈に再構築する力である。
        </Paragraph>
        <Paragraph>
          AI時代に「感覚的に良いものを作れるけど言語化できない人」は厳しくなる。今までは「作れる」こと自体が価値だったから生き残れた。しかしAIが「作る部分」を代替すると、言語化できない限りその人のバリューは発揮できなくなる。設計や実装はできるが「なぜそうしたか」を説明できない人は、AIの出力をレビューする場面でも「なんとなく良さそう」で通してしまう。
        </Paragraph>
        <Paragraph>
          逆に、作れなくても言語化・説明ができる人は、AIと組み合わせることで価値を出せるようになる。
        </Paragraph>

        <SectionHeading title="次の世代をどう育てるか" />
        <Paragraph>
          では後輩エンジニアにどうやって「見抜く力」を身につけさせるか。有効だと考える仮説がある。
        </Paragraph>
        <Paragraph>
          <Bold>AIに書かせたものを必ずレビューさせ、「なぜこうなっているか」を説明させる。</Bold>
        </Paragraph>
        <Paragraph>
          「書く行為」ではなく「説明する行為」を学習の核に据える。これは計算機登場後の数学教育で「計算する行為」から「証明・説明する行為」に学習の重心が移ったのと同じ構造変化だ。
        </Paragraph>
        <Paragraph>
          ただし、これだけで十分かは分からない。特に抽象化・再文脈化力には素養的な要素もあり、訓練方法自体が確立されていない。これもまたパイドロスの問いと同じく、次の世代が実践の中で答えを出していくしかない未決の問いである。
        </Paragraph>

        {/* 第九章 */}
        <ChapterHeading number="第九章" title="「正しい使い方」という幻想" />
        <Paragraph>
          文字の例で言えば、メモの取り方一つとっても人によって最適解は異なる。記憶力が良い人はメモを取らない方が効率的だし、几帳面な人は細かくメモを取った方が効率的だ。
        </Paragraph>

        <SectionHeading title="ノート術ブームの顛末" />
        <Paragraph>
          二〇〇八年に『東大合格生のノートはかならず美しい』（太田あや著、文藝春秋）が刊行され、五十万部を超えるベストセラーとなった。ノートの右側四分の一に線を引いて要点を書くコーネル式に近い手法が紹介され、「東大生はこうやっている」という権威性と具体性から一大ブームになった。書店の参考書売場を超えて、テレビ・雑誌・新聞のあらゆるメディアで取り上げられた。
        </Paragraph>
        <Paragraph>
          しかしブームはすぐに過ぎ去った。後に残ったのは冷静な反論だった。東大専門塾の元講師は「東大生のノートは美しい」を過信しないでいいと述べ、独学で東大に現役合格した者は「ノートはきれいに書かない」「自分の言葉で置き換えることが重要」と語った。結局、ノート術を真似して成績が上がった人はほとんどいなかった。
        </Paragraph>
        <Paragraph>
          そもそも、こんなことは本が出る前から誰もが経験的に知っていたはずだ。学生時代、クラスで一番成績が良い人のノートの取り方を真似している人は必ずいた。でもそれで成績が良くなった人なんていない。形を真似しても、その形に至った思考プロセスまでは真似できないからだ（もっとも、筆者は学生時代にノートなんてものを取ったことがないので実体験としては分からないが）。
        </Paragraph>
        <Paragraph>
          これは当然の帰結だ。東大に行くような人は誰に教わるわけでもなく、自分に合ったノートの取り方を試行錯誤の中で見つける。多くの人が似た結論に行き着くのは「その方法が最適だから真似すべき」なのではなく、「優秀な人が最適化を突き詰めると収斂する」だけの話だ。因果が逆転している。
        </Paragraph>

        <SectionHeading title="同じ幻想の反復" />
        <Paragraph>今、生成AIの世界でも全く同じことが起きている。</Paragraph>
        <BulletList
          items={[
            '「このプロンプトを使えば誰でも高品質なコードが書ける」',
            '「AIを使えば経験がなくても同じように開発できる」',
            '「生成AI活用術10選」',
          ]}
        />
        <Paragraph>
          これらは「東大生のノート術」と同じ構造の幻想だ。表面的な方法論を真似しても、その方法がなぜ有効なのかを理解していなければ機能しない。そして理解している人は、教わらなくても自分でその方法に辿り着く。
        </Paragraph>
        <Paragraph>
          生成AIの使い方は、人に教わって真似するものではない。自分の特性・経験・目的に合わせて、自分で見つけるものだ。万人に効く「正しい使い方」は存在しない。
        </Paragraph>

        {/* おわりに */}
        <ChapterHeading number="おわりに" />
        <Paragraph>
          人類は文字で記憶を、計算機で計算を、インターネットで情報探索を外部化してきた。生成AIはその系譜の到達点であり、「何を入力すべきか」「どう構造化すべきか」「どう出力すべきか」というメタ認知までも外部に預けられるようになった。成果物が出てしまうから労働力の代替と見なされがちだが、本質は認知機能の外部化である。
        </Paragraph>
        <Paragraph>
          ただし、ここに人間の脳の構造的な限界が絡む。電卓には「自分がやった部分」と「機械がやった部分」の明確な境界があった。生成AIにはそれがない。自己奉仕バイアスやIKEA効果といった認知バイアスが介入し、AIの能力を自分の能力だと錯覚する。この錯覚は人間である以上排除できない。排除できない以上、できるのは自覚することだけだ。
        </Paragraph>
        <Paragraph>
          自覚がある人間は疑う姿勢を持てる。疑う姿勢がある人間はAIの出力を見抜ける。見抜ける人間が生産性の余剰を品質に回せば、今まで妥協してきた水準を取り戻せる。これが生成AIの正しい恩恵の受け方だと考える。「楽をするため」ではなく「妥協しないため」に使う。
        </Paragraph>
        <Paragraph>
          そのとき人間に残る仕事は「作ること」ではなく「見抜くこと」——言語化力と抽象化・再文脈化力だ。感覚で良いものを作れても言語化できない人は、AIの出力を「なんとなく良さそう」で通してしまう。逆に作れなくても説明できる人はAIと組んで価値を出せる。能力の重心が移る。
        </Paragraph>
        <Paragraph>
          次の世代がこの力をどう獲得するかは、まだ答えが出ていない。一九七〇年代に電卓が教室に入ったとき、数学のレベルは下がらなかった。学習のルートが変わっただけで到達点は維持された。生成AIでも同じことが起きうる。ただし電卓にはなかった認知バイアスの罠があるから、着地点は同じにはならない。
        </Paragraph>
        <Paragraph>
          そして最も重要なことがある。万人に効く「AIの正しい使い方」は存在しない。それは東大生のノート術を真似しても成績が上がらないのと同じ構造だ。優秀な人が最適化を突き詰めると収斂するだけの話であり、形を真似しても思考プロセスは真似できない。自分に合った使い方は、自分で見つけるしかない。
        </Paragraph>
        <Paragraph>
          結局、生成AIと付き合う上で必要なのは二つだけだ。
          <Bold>AIの能力を自分の能力だと自惚れない自覚</Bold>と、
          <Bold>自分なりの使い方を試行錯誤で見つけていく姿勢</Bold>
          。それ以上のことは、まだ誰にも分からない。二四〇〇年前にソクラテスが文字を批判したとき、その言葉が文字によって永遠に残ることを彼は知らなかった。我々もまた、生成AIがもたらす帰結の全貌を知らないまま、歴史の渦中にいる。
        </Paragraph>
      </Box>
    </Stack>
  );
};

// --- サブコンポーネント ---

const ChapterHeading: React.FC<{ number: string; title?: string }> = ({ number, title }) => (
  <Box sx={{ mt: 5, mb: 2 }}>
    <Typography
      sx={{
        fontSize: 11,
        fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
        letterSpacing: '0.3em',
        color: '#888',
        mb: 0.5,
      }}
    >
      {number}
    </Typography>
    {title && (
      <Typography
        component="h2"
        sx={{
          fontSize: 19,
          fontWeight: 700,
          fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
          color: '#1A1A1A',
          lineHeight: 1.6,
        }}
      >
        {title}
      </Typography>
    )}
  </Box>
);

const SectionHeading: React.FC<{ title: string }> = ({ title }) => (
  <Typography
    component="h3"
    sx={{
      fontSize: 15,
      fontWeight: 700,
      fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
      color: '#1A1A1A',
      mt: 3,
      mb: 1.5,
      lineHeight: 1.6,
    }}
  >
    {title}
  </Typography>
);

const Paragraph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    component="p"
    sx={{
      fontSize: 14,
      fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
      color: '#1A1A1A',
      lineHeight: 2.0,
      textIndent: '1em',
      mb: 1.8,
    }}
  >
    {children}
  </Typography>
);

const Bold: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ fontWeight: 700 }}>
    {children}
  </Box>
);

const Blockquote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      borderLeft: '3px solid #C8C3B8',
      pl: 2,
      my: 2,
    }}
  >
    <Typography
      sx={{
        fontSize: 14,
        fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
        color: '#333333',
        lineHeight: 2.0,
      }}
    >
      {children}
    </Typography>
  </Box>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <Box component="ul" sx={{ pl: 2, my: 1.5, listStyle: 'none' }}>
    {items.map((item, i) => (
      <Box
        component="li"
        key={i}
        sx={{
          fontSize: 14,
          fontFamily: '"Noto Serif JP", "游明朝", "YuMincho", serif',
          color: '#1A1A1A',
          lineHeight: 2.0,
          '&::before': {
            content: '"—"',
            mr: 1,
            color: '#888',
          },
        }}
      >
        {item}
      </Box>
    ))}
  </Box>
);

const ArticleTable: React.FC<{ headers: string[]; rows: string[][] }> = ({ headers, rows }) => (
  <Box sx={{ overflowX: 'auto', my: 2 }}>
    <Box
      component="table"
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        fontFamily: '"Noto Sans JP", sans-serif',
        fontSize: 13,
        '& th, & td': {
          border: '1px solid #D0CCC5',
          p: '12px 16px',
          textAlign: 'left',
        },
        '& th': {
          bgcolor: '#F0EDE8',
          fontWeight: 700,
        },
      }}
    >
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td key={j}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
);

export default ThoughtsAi;
